import React, { useState, useCallback, useEffect } from 'react';
import { CalculatorPokemon } from '../../hooks/useCalculator';
import typesData from '../../data/types.json';
import './MovesOverview.css';

// Extract type names for effectiveness calculations
const typeEffectivenessData = typesData;

// Type effectiveness calculation (matching Python logic)
const getTypeEffectiveness = (attackingType: string, defendingTypes: string[]): number => {
  let effectiveness = 1.0;

  // Find the attacking type data
  const attackingTypeData = typeEffectivenessData.find(
    type => type.english === attackingType
  );

  if (!attackingTypeData) {
    return effectiveness;
  }

  // Calculate effectiveness against each defending type
  defendingTypes.forEach(defendingType => {
    if (!defendingType || defendingType === "None" || defendingType === "") {
      return; // Skip empty/None types
    }

    if (attackingTypeData.effective?.includes(defendingType)) {
      effectiveness *= 2.0;
    } else if (attackingTypeData.ineffective?.includes(defendingType)) {
      effectiveness *= 0.5;
    } else if (attackingTypeData.no_effect?.includes(defendingType)) {
      effectiveness *= 0.0;
    }
  });

  return effectiveness;
};

// Simple damage calculation (matching Python logic)
const calculateDamageRange = (
  level: number,
  attack: number,
  defense: number,
  power: number,
  typeEffectiveness: number,
  stab: number,
  critical: number,
  weather: number = 1.0
): number[] => {
  if (power === 0 || !attack || !defense) return [];

  // Base damage calculation
  const baseDamage = Math.floor(
    (Math.floor((Math.floor(2 * level / 5 + 2) * attack * power / defense) / 50) + 2) *
    stab * typeEffectiveness * critical * weather
  );

  // Apply random factor (85-100%)
  const damageRange: number[] = [];
  for (let i = 85; i <= 100; i++) {
    damageRange.push(Math.floor(baseDamage * i / 100));
  }

  return damageRange;
};

interface MovesOverviewProps {
  leftPokemon: CalculatorPokemon;
  rightPokemon: CalculatorPokemon;
  battleConditions: any;
}

interface MoveResult {
  damage: string;
  range: number[];
  summary: string;
  typeEffect: number;
  isStab: boolean;
  moveType: string;
  ohkoLabel: string;
}

const effLabel = (e: number): string => {
  if (e === 0) return 'Immune';
  if (e === 0.25) return '¼×';
  if (e === 0.5) return '½×';
  if (e === 2) return '2×';
  if (e === 4) return '4×';
  return '';
};

const effClass = (e: number): string => {
  if (e === 0) return 'immune';
  if (e < 1) return 'resist';
  if (e === 2) return 'super';
  if (e === 4) return 'quad';
  return '';
};

const MovesOverview: React.FC<MovesOverviewProps> = ({
  leftPokemon,
  rightPokemon,
  battleConditions
}) => {
  const [leftSelectedMove, setLeftSelectedMove] = useState<number | null>(null);
  const [rightSelectedMove, setRightSelectedMove] = useState<number | null>(null);

  // Reset selected moves when Pokemon change
  useEffect(() => {
    setLeftSelectedMove(null);
    setRightSelectedMove(null);
  }, [leftPokemon.pokemon?.id, rightPokemon.pokemon?.id]);

  // Calculate damage for a specific move
  const calculateMoveDamage = useCallback((
    attacker: CalculatorPokemon,
    defender: CalculatorPokemon,
    moveIndex: number
  ): MoveResult => {
    const move = attacker.moves[moveIndex];
    if (!move || !move.name || move.name === "(No Move)" || !move.power) {
      return { damage: "", range: [], summary: "", typeEffect: 1, isStab: false, moveType: "", ohkoLabel: "" };
    }

    // Get stats
    const level = attacker.level;
    const attackStat = move.category === "Physical" ?
      (attacker.calculatedStats?.attack || 0) :
      (attacker.calculatedStats?.sp_attack || 0);
    const defenseStat = move.category === "Physical" ?
      (defender.calculatedStats?.defense || 0) :
      (defender.calculatedStats?.sp_defense || 0);

    // Get defending types (from Pokemon data + any overrides)
    const defendingTypes: string[] = [
      defender.types?.[0] || defender.pokemon?.types?.[0] || "",
      defender.types?.[1] || defender.pokemon?.types?.[1] || "",
      defender.types?.[2] || ""
    ].filter(Boolean);

    // Get attacking types for STAB
    const attackingTypes: string[] = [
      attacker.types?.[0] || attacker.pokemon?.types?.[0] || "",
      attacker.types?.[1] || attacker.pokemon?.types?.[1] || "",
      attacker.types?.[2] || ""
    ].filter(Boolean);

    // Calculate modifiers
    const typeEffect = getTypeEffectiveness(move.type, defendingTypes);
    const stab = attackingTypes.includes(move.type) ? 1.5 : 1.0;
    const crit = move.crit ? 1.5 : 1.0;

    // Calculate damage range
    const damageRange = calculateDamageRange(
      level,
      attackStat,
      defenseStat,
      move.power,
      typeEffect,
      stab,
      crit
    );

    if (damageRange.length === 0) {
      return { damage: "", range: [], summary: "", typeEffect: typeEffect, isStab: stab > 1, moveType: move.type || "", ohkoLabel: "" };
    }

    const minDamage = Math.min(...damageRange);
    const maxDamage = Math.max(...damageRange);
    const defenderHP = defender.calculatedStats?.hp || 1;

    const minPercent = (minDamage / defenderHP) * 100;
    const maxPercent = (maxDamage / defenderHP) * 100;

    const damage = `${minPercent.toFixed(1)}% - ${maxPercent.toFixed(1)}%`;

    // OHKO calculation
    const totalRolls = damageRange.length;
    const ohkoRolls = damageRange.filter(d => d >= defenderHP).length;
    let ohkoLabel = '';
    if (ohkoRolls === totalRolls) {
      ohkoLabel = '1HKO';
    } else if (ohkoRolls > 0) {
      ohkoLabel = `1HKO (${Math.round(ohkoRolls / totalRolls * 100)}%)`;
    } else {
      for (let n = 2; n <= 6; n++) {
        if (minDamage * n >= defenderHP) {
          ohkoLabel = `${n}HKO`;
          break;
        } else if (maxDamage * n >= defenderHP) {
          ohkoLabel = `~${n}HKO`;
          break;
        }
      }
    }

    // Create summary text (matching Python format) - using EV values
    const attackLabel = move.category === "Physical" ? "Atk" : "Sp. Atk";
    const defenseLabel = move.category === "Physical" ? "Def" : "Sp. Def";
    const attackerName = attacker.pokemon?.name || "Unknown";
    const defenderName = defender.pokemon?.name || "Unknown";

    // Use EV values instead of calculated stats
    const attackEV = move.category === "Physical" ? attacker.evs.attack : attacker.evs.sp_attack;
    const defenseEV = move.category === "Physical" ? defender.evs.defense : defender.evs.sp_defense;
    const hpEV = defender.evs.hp;

    const summary = `${attackEV} ${attackLabel} ${attackerName} ${move.name} vs. ` +
                   `${hpEV} HP / ${defenseEV} ${defenseLabel} ${defenderName}: ` +
                   `${minDamage} - ${maxDamage} (${minPercent.toFixed(1)}% - ${maxPercent.toFixed(1)}%)`;

    return { damage, range: damageRange, summary, typeEffect, isStab: stab > 1, moveType: move.type || "", ohkoLabel };
  }, []);

  // Get current selected move result for detailed display
  const getSelectedMoveResult = (): { result: MoveResult; isLeft: boolean } | null => {
    if (leftSelectedMove !== null) {
      return {
        result: calculateMoveDamage(leftPokemon, rightPokemon, leftSelectedMove),
        isLeft: true
      };
    }
    if (rightSelectedMove !== null) {
      return {
        result: calculateMoveDamage(rightPokemon, leftPokemon, rightSelectedMove),
        isLeft: false
      };
    }
    return null;
  };

  const selectedResult = getSelectedMoveResult();

  return (
    <div className="moves-overview">
      <div className="moves-header">
        <span className="column-label header-left">Move / Type</span>
        <span className="column-label header-dmg">Damage</span>
        <span className="column-label header-dmg">Damage</span>
        <span className="column-label header-right">Type / Move</span>
      </div>

      <div className="moves-content">
        {[0, 1, 2, 3].map((moveIndex) => {
          const leftMove = leftPokemon.moves[moveIndex];
          const rightMove = rightPokemon.moves[moveIndex];
          const leftResult = calculateMoveDamage(leftPokemon, rightPokemon, moveIndex);
          const rightResult = calculateMoveDamage(rightPokemon, leftPokemon, moveIndex);

          // Debug logging
          if (moveIndex === 0) {
            console.log('Move Debug:', {
              leftMove: leftMove?.name,
              rightMove: rightMove?.name,
              leftPower: leftMove?.power,
              rightPower: rightMove?.power,
              leftResult: leftResult.damage,
              rightResult: rightResult.damage
            });
          }

          const leftEff = effLabel(leftResult.typeEffect);
          const rightEff = effLabel(rightResult.typeEffect);

          return (
            <div key={moveIndex} className="move-row">
              {/* LEFT side */}
              <div className="move-side move-side--left">
                <button
                  className={`move-button left ${leftSelectedMove === moveIndex ? 'selected' : ''}`}
                  onClick={() => {
                    setLeftSelectedMove(leftSelectedMove === moveIndex ? null : moveIndex);
                    setRightSelectedMove(null);
                  }}
                  disabled={!leftMove?.name || leftMove.name === "(No Move)"}
                >
                  <span className="move-btn-name">{leftMove?.name || ""}</span>
                  {leftResult.moveType && (
                    <span className="move-btn-badges">
                      <span className={`type-pill type-${leftResult.moveType.toLowerCase()}`}>{leftResult.moveType}</span>
                      {leftResult.isStab && <span className="stab-pill">STAB</span>}
                      {leftEff && <span className={`eff-pill eff-pill--${effClass(leftResult.typeEffect)}`}>{leftEff}</span>}
                    </span>
                  )}
                </button>
              </div>

              {/* Damage columns */}
              <div className="damage-result">
                <span className="dmg-pct">
                  {leftResult.damage || "--"}{leftResult.ohkoLabel ? ` – ${leftResult.ohkoLabel}` : ""}
                </span>
              </div>
              <div className="damage-result">
                <span className="dmg-pct">
                  {rightResult.damage || "--"}{rightResult.ohkoLabel ? ` – ${rightResult.ohkoLabel}` : ""}
                </span>
              </div>

              {/* RIGHT side */}
              <div className="move-side move-side--right">
                <button
                  className={`move-button right ${rightSelectedMove === moveIndex ? 'selected' : ''}`}
                  onClick={() => {
                    setRightSelectedMove(rightSelectedMove === moveIndex ? null : moveIndex);
                    setLeftSelectedMove(null);
                  }}
                  disabled={!rightMove?.name || rightMove.name === "(No Move)"}
                >
                  {rightResult.moveType && (
                    <span className="move-btn-badges">
                      {rightEff && <span className={`eff-pill eff-pill--${effClass(rightResult.typeEffect)}`}>{rightEff}</span>}
                      {rightResult.isStab && <span className="stab-pill">STAB</span>}
                      <span className={`type-pill type-${rightResult.moveType.toLowerCase()}`}>{rightResult.moveType}</span>
                    </span>
                  )}
                  <span className="move-btn-name right">{rightMove?.name || ""}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedResult && (
        <div className="damage-summary">
          <div className="summary-text">
            {selectedResult.result.summary}
          </div>
          {selectedResult.result.range.length > 0 && (
            <div className="damage-values">
              {selectedResult.result.range.join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MovesOverview;
